import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { get } from "../../lib/api";
import { canAssignRole, effectivePermissions, toggleRole } from "../../lib/operator";
import { permissionCount } from "../../lib/permissions";
import { useSession } from "../../lib/session";
import type { Role } from "../../lib/types";
import { PermissionPicker } from "../access/PermissionPicker";
import { ErrorNotice } from "../ui/Feedback";
import { MicroLabel } from "../ui/Text";

export const ROLES_KEY = ["roles"] as const;

/** `GET /roles`, shared by the operator forms and the roles page. */
export function useRoles(enabled = true) {
  return useQuery({ queryKey: ROLES_KEY, queryFn: () => get<readonly Role[]>("/roles"), enabled });
}

/**
 * What an operator may do: roles first, direct permissions behind a disclosure.
 *
 * Roles lead because they are the normal answer. A direct permission is the
 * exception — one person who needs one thing — and an exception that sits beside
 * the rule with equal weight gets used as the rule, until nobody can say why a
 * person can do what they can. So it starts collapsed unless the operator already
 * has some, in which case hiding them would hide part of the account.
 *
 * Underneath both, a running total of what the account would be able to do, so
 * the effect of ticking a role is visible before saving it.
 */
export function AccessPicker({
  roleIds,
  permissions,
  onRolesChange,
  onPermissionsChange,
  error,
}: {
  readonly roleIds: readonly string[];
  readonly permissions: readonly string[];
  readonly onRolesChange: (next: readonly string[]) => void;
  readonly onPermissionsChange: (next: readonly string[]) => void;
  readonly error?: string | undefined;
}) {
  const { principal } = useSession();
  const held = principal?.permissions ?? [];
  const roles = useRoles();
  const [showDirect, setShowDirect] = useState(permissions.length > 0);

  const chosen = (roles.data ?? []).filter((role) => roleIds.includes(role.id));
  const effective = effectivePermissions(chosen, permissions);

  return (
    <div className="flex flex-col gap-5">
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="pb-1">
          <MicroLabel>Roles</MicroLabel>
        </legend>
        {roles.isError ? <ErrorNotice error={roles.error} /> : null}
        {roles.isPending ? <span className="text-data text-ink-faint">Loading roles…</span> : null}
        {(roles.data ?? []).map((role) => {
          const assignable = canAssignRole(held, role);
          return (
            <label
              key={role.id}
              className={`flex items-start gap-2 text-body ${assignable ? "text-ink" : "text-ink-faint"}`}
            >
              <input
                type="checkbox"
                checked={roleIds.includes(role.id)}
                disabled={!assignable}
                onChange={() => onRolesChange(toggleRole(roleIds, role.id))}
                className="mt-1 size-4 accent-signal"
              />
              <span>
                {role.name}
                <span className="block text-micro tracking-normal normal-case text-ink-faint">
                  {!assignable
                    ? "It carries permissions you don't hold, so you can't assign it."
                    : role.grantsAll
                      ? "Every permission, including ones added later."
                      : (role.description ?? `${role.permissions.length} permissions`)}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          aria-expanded={showDirect}
          onClick={() => setShowDirect((open) => !open)}
          className="self-start border-0 bg-transparent p-0 text-data font-medium text-ink underline decoration-rule decoration-1 underline-offset-4 hover:decoration-ink"
        >
          {showDirect ? "Hide" : "Add"} direct permissions
          {permissions.length > 0 ? ` (${permissions.length})` : ""}
        </button>
        {showDirect ? (
          <PermissionPicker
            legend="Direct permissions — on top of the roles"
            value={permissions}
            held={held}
            onChange={onPermissionsChange}
          />
        ) : null}
      </div>

      <p className="m-0 text-data text-ink-muted">
        Can do <span className="font-semibold text-ink">{permissionCount(effective)}</span>{" "}
        permissions.
      </p>

      {error === undefined ? null : (
        <span role="alert" className="text-micro tracking-normal normal-case text-signal">
          {error}
        </span>
      )}
    </div>
  );
}
